import React from 'react';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Button } from '@michelin/acid-components';

/**
 * Shared presentational building blocks for the Main Preferences sub-areas.
 * Data is supplied by each sub-area; this file keeps reusable layout/styling
 * primitives only.
 */

export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Panel ------------------------------------------------------------------
    panel: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      marginBottom: '1rem',
      overflow: 'hidden',
    },
    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.9rem 1.25rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    panelTitle: {
      fontSize: '0.95rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    panelBody: {
      padding: '1.25rem',
    },
    panelBodyFlush: {
      padding: 0,
    },
    // Area header ------------------------------------------------------------
    areaHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.85rem',
      marginBottom: '1rem',
    },
    areaIcon: {
      flexShrink: 0,
      width: 44,
      height: 44,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 22,
    },
    areaHeaderText: {
      flex: 1,
      minWidth: 0,
    },
    areaTitle: {
      fontSize: '1.35rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: theme.palette.text.primary,
    },
    areaSubtitle: {
      marginTop: 2,
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
    },
    // Status pill ------------------------------------------------------------
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: '0.72rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    pillDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      marginRight: 4,
    },
    // Toggle row -------------------------------------------------------------
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.85rem 0',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    rowLabel: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    rowDescription: {
      marginTop: 2,
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
    rowValue: {
      fontSize: '0.9rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    // Alert banner -----------------------------------------------------------
    banner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.85rem 1.1rem',
      borderRadius: 10,
      marginBottom: '1rem',
    },
    bannerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      fontSize: '0.85rem',
      fontWeight: 600,
    },
    // Why it matters ---------------------------------------------------------
    whyItMatters: {
      display: 'flex',
      gap: '0.6rem',
      padding: '0.85rem 1.1rem',
      borderRadius: 10,
      backgroundColor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`,
      fontSize: '0.82rem',
      color: theme.palette.text.secondary,
      marginBottom: '1rem',
    },
    // Footer link ------------------------------------------------------------
    footerButton: {
      textTransform: 'none',
      marginTop: '0.25rem',
    },
  }),
);

const TONE_COLORS: Record<StatusTone, { bg: string; fg: string }> = {
  success: { bg: '#e6f4ea', fg: '#1e7e34' },
  warning: { bg: '#fff4e5', fg: '#b26a00' },
  danger: { bg: '#fdecea', fg: '#c0392b' },
  neutral: { bg: '#eef0f3', fg: '#4b5563' },
};

// --- Status pill ------------------------------------------------------------
interface StatusPillProps {
  label: string;
  tone?: StatusTone;
  showDot?: boolean;
}

export function StatusPill({ label, tone = 'neutral', showDot = true }: StatusPillProps) {
  const classes = useStyles();
  const colors = TONE_COLORS[tone];
  return (
    <span className={classes.pill} style={{ backgroundColor: colors.bg, color: colors.fg }}>
      {showDot && <span className={classes.pillDot} style={{ backgroundColor: colors.fg }} />}
      {label}
    </span>
  );
}

// --- Area header ------------------------------------------------------------
interface AreaHeaderProps {
  icon: React.ReactNode;
  iconColor?: string;
  title: string;
  subtitle?: string;
  status?: { label: string; tone: StatusTone };
}

export function AreaHeader({ icon, iconColor = '#eef0f3', title, subtitle, status }: AreaHeaderProps) {
  const classes = useStyles();
  return (
    <div className={classes.areaHeader}>
      <div className={classes.areaIcon} style={{ backgroundColor: iconColor }}>
        {icon}
      </div>
      <div className={classes.areaHeaderText}>
        <Typography className={classes.areaTitle}>{title}</Typography>
        {subtitle && <Typography className={classes.areaSubtitle}>{subtitle}</Typography>}
      </div>
      {status && <StatusPill label={status.label} tone={status.tone} />}
    </div>
  );
}

// --- Panel ------------------------------------------------------------------
interface PanelProps {
  title?: string;
  action?: React.ReactNode;
  flush?: boolean;
  children: React.ReactNode;
}

export function Panel({ title, action, flush = false, children }: PanelProps) {
  const classes = useStyles();
  return (
    <div className={classes.panel}>
      {(title || action) && (
        <div className={classes.panelHeader}>
          <Typography className={classes.panelTitle}>{title}</Typography>
          {action}
        </div>
      )}
      <div className={flush ? classes.panelBodyFlush : classes.panelBody}>{children}</div>
    </div>
  );
}

// --- Toggle row -------------------------------------------------------------
interface ToggleRowProps {
  label: string;
  description?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void | Promise<void>;
}

export function ToggleRow({ label, description, checked = false, onChange }: ToggleRowProps) {
  const classes = useStyles();
  return (
    <div className={classes.toggleRow}>
      <div>
        <Typography className={classes.rowLabel}>{label}</Typography>
        {description && <Typography className={classes.rowDescription}>{description}</Typography>}
      </div>
      <Switch
        checked={checked}
        color="primary"
        onChange={(_event, nextChecked) => {
          if (onChange) {
            void onChange(nextChecked);
          }
        }}
      />
    </div>
  );
}

// --- Value row --------------------------------------------------------------
interface ValueRowProps {
  label: string;
  description?: string;
  value: React.ReactNode;
  valueTone?: StatusTone;
}

export function ValueRow({ label, description, value, valueTone }: ValueRowProps) {
  const classes = useStyles();
  const color = valueTone ? TONE_COLORS[valueTone].fg : undefined;
  return (
    <div className={classes.toggleRow}>
      <div>
        <Typography className={classes.rowLabel}>{label}</Typography>
        {description && <Typography className={classes.rowDescription}>{description}</Typography>}
      </div>
      <Typography className={classes.rowValue} style={{ color }}>
        {value}
      </Typography>
    </div>
  );
}

// --- Alert banner -----------------------------------------------------------
interface AlertBannerProps {
  message: React.ReactNode;
  tone?: Exclude<StatusTone, 'neutral'>;
  action?: React.ReactNode;
}

export function AlertBanner({ message, tone = 'danger', action }: AlertBannerProps) {
  const classes = useStyles();
  const colors = TONE_COLORS[tone];
  return (
    <div className={classes.banner} style={{ backgroundColor: colors.bg }}>
      <div className={classes.bannerContent} style={{ color: colors.fg }}>
        <span aria-hidden>&#9888;</span>
        <span>{message}</span>
      </div>
      {action}
    </div>
  );
}

// --- Why it matters ---------------------------------------------------------
export function WhyItMatters({ children }: { children: React.ReactNode }) {
  const classes = useStyles();
  return (
    <div className={classes.whyItMatters}>
      <span aria-hidden>&#128161;</span>
      <span>
        <strong>Why it matters.</strong> {children}
      </span>
    </div>
  );
}

// --- Open full area link ----------------------------------------------------
export function OpenFullButton({ label, onClick }: { label: string; onClick?: () => void }) {
  const classes = useStyles();
  return (
    <Button variant="contained" size="small" className={classes.footerButton} onClick={onClick}>
      {label} &rarr;
    </Button>
  );
}
